import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { performanceMonitor } from "@/services/performance-monitor";
import { CACHE_CONFIG } from './cache-config';
import { refreshAuthToken, clearAuth, isAuthenticated } from './auth';
import { withCsrfHeader, isCsrfRejection, CSRF_RELOGIN_MESSAGE } from './authed-fetch';
import { ApiError, extractFriendly } from './api-error';

function redirectToLogin() {
  // Never redirect-loop while already on the login page
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}
import { reportError } from '@/services/error-reporter';

async function throwIfResNotOk(res: Response, method?: string) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    // Report server-side failures (5xx) to the admin error log (task #1).
    // Always use the full raw body in the admin record so internal detail
    // is preserved there even though only a generic message goes to the UI.
    if (res.status >= 500) {
      try {
        const path = new URL(res.url, window.location.origin).pathname;
        reportError({
          source: 'client-api',
          message: `API ${method || 'GET'} ${path} failed with ${res.status}: ${text.slice(0, 500)}`,
          route: path,
          method: method || 'GET',
          statusCode: res.status,
        });
      } catch { /* never throw from error pipeline */ }
    }
    // Extract a human-readable message while preserving the full structured
    // body on the thrown ApiError so programmatic callers (e.g. transcription
    // error branches that read body.code / body.missing / body.reason) still
    // work without re-parsing the message string.
    const { friendly, body } = extractFriendly(text, res.statusText);
    throw new ApiError(res.status, friendly, body, text);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: HeadersInit = {};
  
  // Check if data is FormData (for file uploads)
  const isFormData = data instanceof FormData;
  
  if (data && !isFormData) {
    headers["Content-Type"] = "application/json";
  }
  // Note: Don't set Content-Type for FormData - the browser will set it with the boundary
  // Auth rides on httpOnly cookies (credentials: "include") — no Authorization header.

  let body: any;
  if (isFormData) {
    body = data;
  } else if (data) {
    body = JSON.stringify(data);
  } else {
    body = undefined;
  }

  let res = await fetch(url, {
    method,
    headers: withCsrfHeader(method, headers),
    body,
    credentials: "include",
  });

  // If 401 while we believe we're logged in, try one cookie refresh.
  // (isAuthenticated() mirrors the old "do we have a token" gate so
  // logged-out pages don't hammer /api/auth/refresh or redirect-loop.)
  if (res.status === 401 && isAuthenticated() && !url.includes('/api/auth/refresh')) {
    const refreshed = await refreshAuthToken();
    if (refreshed) {
      // Re-read the CSRF cookie — the refresh rotates it alongside the auth cookies.
      res = await fetch(url, {
        method,
        headers: withCsrfHeader(method, headers),
        body,
        credentials: "include",
      });
    } else {
      clearAuth();
      redirectToLogin();
    }
  }

  // CSRF 403 (csrf-token cookie expired/cleared while auth cookies survive):
  // one silent refresh re-issues the csrf cookie (the endpoint is exempt),
  // then retry once. If it still fails, prompt a clear re-login.
  if (await isCsrfRejection(res)) {
    const refreshed = await refreshAuthToken();
    if (refreshed) {
      res = await fetch(url, {
        method,
        headers: withCsrfHeader(method, headers), // re-reads the fresh cookie
        body,
        credentials: "include",
      });
    }
    if (await isCsrfRejection(res)) {
      clearAuth();
      redirectToLogin();
      throw new Error(CSRF_RELOGIN_MESSAGE);
    }
  }

  await throwIfResNotOk(res, method);
  return res;
}

function buildUrlFromQueryKey(queryKey: readonly unknown[]): string {
  if (queryKey.length === 0) {
    throw new Error('Query key cannot be empty');
  }

  let url = String(queryKey[0]);
  const params = new URLSearchParams();
  
  for (let i = 1; i < queryKey.length; i++) {
    const element = queryKey[i];
    
    if (element === null || element === undefined) {
      continue;
    }
    
    if (typeof element === 'object' && !Array.isArray(element)) {
      Object.entries(element).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    } else {
      url += '/' + String(element);
    }
  }
  
  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = buildUrlFromQueryKey(queryKey);
    let res = await fetch(url, {
      credentials: "include",
    });

    // If 401 while we believe we're logged in, try one cookie refresh
    if (res.status === 401 && isAuthenticated()) {
      const refreshed = await refreshAuthToken();
      if (refreshed) {
        res = await fetch(url, {
          credentials: "include",
        });
      } else if (unauthorizedBehavior === "throw") {
        clearAuth();
        redirectToLogin();
      }
    }

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: CACHE_CONFIG.dynamic.staleTime, // Enhanced cache config: 5 minutes default
      gcTime: CACHE_CONFIG.dynamic.gcTime,       // Enhanced cache config: 10 minutes default
      retry: 1,                                   // Reduced retry attempts for faster feedback
      retryDelay: 1000,                          // 1 second retry delay
      networkMode: 'online',                     // Only run when online
    },
    mutations: {
      retry: false,
      networkMode: 'online',
    },
  },
});

// Install React Query interceptor if performance logging is enabled
if (performanceMonitor.isEnabled()) {
  performanceMonitor.installQueryInterceptor(queryClient);
}
